/**
 * The frame of every sign-in screen (K3 «Kirish»): the navy brand panel with the route from a
 * lead to a payment, and the form column beside it. On a phone the panel stacks above the form.
 */
import type { JSX, ReactNode } from "react";

import { useTranslation } from "@/i18n";
import { Logo, RouteLine } from "@/ui/brand";

export function AuthFrame({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  return (
    <main className="grid min-h-dvh grid-cols-[minmax(0,1fr)] bg-canvas md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="flex flex-col gap-7 overflow-hidden bg-navy px-5 pt-6 pb-7 text-btn-primary-ink md:px-12 md:py-10">
        <div className="flex items-center gap-2.5">
          <Logo kind="dark" size={28} />
          <b className="text-lg font-semibold tracking-display">{t("app.name")}</b>
        </div>
        <div className="flex max-w-110 flex-1 flex-col justify-center gap-5">
          <h1 className="text-hero-sm leading-[1.1] font-semibold tracking-kpi text-balance md:text-hero">
            {t("authPanel.title")}
          </h1>
          <p className="text-lg leading-6 text-pretty text-navy-ink">{t("authPanel.body")}</p>
          <RouteLine
            onNavy
            className="mt-2"
            steps={[t("authPanel.route.lead"), t("authPanel.route.offer"), t("authPanel.route.payment")]}
          />
        </div>
        <span className="text-xs text-navy-ink-muted">{t("authPanel.footer")}</span>
      </aside>
      <section className="flex items-center justify-center px-5 pt-7 pb-18 md:p-10">
        <div className="flex w-full max-w-95 flex-col gap-4.5">{children}</div>
      </section>
    </main>
  );
}

/** The form's heading: one title, and a line under it where the screen has one to say. */
export function AuthHeading({ title, lead }: { title: string; lead?: string | undefined }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-h2 font-semibold text-ink">{title}</h2>
      {lead === undefined ? null : <span className="text-sm text-ink-muted">{lead}</span>}
    </div>
  );
}

/** A field's small label above its input (12/500 in the prototype). */
export const AUTH_LABEL = "text-xs font-medium text-ink-muted";
export const AUTH_FIELD = "flex flex-col gap-1.5";
export const AUTH_INPUT = "h-10";
export const AUTH_LINK = "text-xs font-medium text-accent-ink hover:text-accent-hover";

/** A sentence about what went wrong, with the dot the prototype puts before it. */
export function AuthError({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span role="alert" className="flex items-center gap-2 text-sm text-danger-ink">
      <span aria-hidden="true" className="size-2 shrink-0 rounded-pill bg-danger" />
      {children}
    </span>
  );
}
